'use strict'
import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Box,
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  Tooltip
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import TuneIcon from '@material-ui/icons/Tune'
import CloseIcon from '@material-ui/icons/Close'
import FlashOnIcon from '@material-ui/icons/FlashOn'
import CrewDayRouteDetail from './CrewDayRouteDetail'
import FleetCharts from './FleetCharts'
import TwoWeekView from './TwoWeekView'
import { DFButton } from '../../df'

const toYmd = (value) => {
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

/* ── Headline delta metrics ─────────────────────────────────────── */
const FLEET_METRIC_ROWS = [
  { label: 'Properties', key: 'totalProperties', unit: '' },
  { label: 'Service Hrs', key: 'totalServiceHours', unit: ' hrs' },
  { label: 'Actual Svc Hrs', key: 'totalActualServiceHours', unit: ' hrs' },
  { label: 'Drive Time', key: 'totalDriveMinutes', unit: ' min' },
  { label: 'Drive Miles', key: 'totalDriveMiles', unit: ' mi' },
  { label: 'Avg Crew Load', key: 'avgCrewLoad', unit: ' min' },
  { label: 'Total OT', key: 'totalOT', unit: ' min' }
]

const LEVEL_OPTIONS = [
  { value: 'fleet', label: 'Fleet' },
  { value: 'crew-week', label: 'Route·Wk' },
  { value: 'crew-day', label: 'Route·Day' },
  { value: 'two-week', label: '2-Week' }
]

export default function CompareTab ({
  url,
  selectedScenarioId,
  scenarios,
  crews,
  crewIds,
  routes,
  onSnackbar
}) {
  const [compareToScenarioId, setCompareToScenarioId] = useState('')
  const [compareLevel, setCompareLevel] = useState('fleet')
  const [compareCrewId, setCompareCrewId] = useState('')
  const [compareDate, setCompareDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [scenarioCompare, setScenarioCompare] = useState(null)
  const [loadingCompare, setLoadingCompare] = useState(false)
  const [scenarioDiff, setScenarioDiff] = useState(null)
  const [loadingDiff, setLoadingDiff] = useState(false)
  const [recomputingExact, setRecomputingExact] = useState(false)
  const [exactRecompute, setExactRecompute] = useState(null)
  const [settingsOpen, setSettingsOpen] = useState(false)


  useEffect(() => {
    if (compareCrewId) return
    // Prefer first route's crewId; fall back to first crew
    const firstRoute = (routes || [])[0]
    if (firstRoute?.crewId) { setCompareCrewId(firstRoute.crewId); return }
    const firstCrew = crews[0]?._id || ''
    if (firstCrew) setCompareCrewId(firstCrew)
  }, [compareCrewId, crews, routes])

  const fetchScenarioDiff = useCallback(async (scenarioId, { silentError = false } = {}) => {
    if (!scenarioId) { setScenarioDiff(null); return null }
    setLoadingDiff(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios/${scenarioId}/diff`, {
        params: { includeMetricsDelta: true }
      })
      setScenarioDiff(response.data || null)
      return response.data || null
    } catch (err) {
      console.error('Error loading scenario diff:', err)
      if (!silentError) {
        onSnackbar(err.response?.data?.message || err.message || 'Failed to load diff', 'error')
      }
      setScenarioDiff(null)
      return null
    } finally {
      setLoadingDiff(false)
    }
  }, [url, onSnackbar])

  const fetchScenarioCompare = useCallback(async (scenarioId, opts = {}) => {
    if (!scenarioId) { setScenarioCompare(null); return null }
    const level = opts.level || compareLevel
    const crewId = opts.crewId || compareCrewId
    const date = opts.date || compareDate
    const compareTo = opts.compareTo ?? compareToScenarioId

    const params = { level: level || 'fleet' }
    if (compareTo) params.compareToScenarioId = compareTo
    if (params.level !== 'fleet' && crewId) params.crewId = crewId
    if (params.level === 'crew-day') params.date = date || new Date().toISOString().slice(0, 10)
    if (params.level === 'two-week') params.weekOf = date || new Date().toISOString().slice(0, 10)

    setLoadingCompare(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios/${scenarioId}/compare`, { params })
      setScenarioCompare(response.data || null)
      return response.data || null
    } catch (err) {
      console.error('Error loading comparison:', err)
      if (!opts.silentError) {
        onSnackbar(err.response?.data?.message || err.message || 'Failed to load comparison', 'error')
      }
      setScenarioCompare(null)
      return null
    } finally {
      setLoadingCompare(false)
    }
  }, [compareCrewId, compareDate, compareLevel, compareToScenarioId, url, onSnackbar])

  useEffect(() => {
    if (!selectedScenarioId) {
      setScenarioDiff(null)
      setScenarioCompare(null)
      setExactRecompute(null)
      return
    }
    setExactRecompute(null)
    fetchScenarioDiff(selectedScenarioId, { silentError: true })
    fetchScenarioCompare(selectedScenarioId, { silentError: true })
  }, [selectedScenarioId, fetchScenarioDiff, fetchScenarioCompare])

  useEffect(() => {
    if (!selectedScenarioId) return
    fetchScenarioCompare(selectedScenarioId, { silentError: true })
  }, [compareCrewId, compareDate, compareLevel, compareToScenarioId, fetchScenarioCompare, selectedScenarioId])

  const runRecomputeExact = async () => {
    if (!selectedScenarioId) return
    const body = {}
    if (compareLevel === 'crew-day') {
      if (!compareCrewId) { onSnackbar('Pick a route first for route-day exact recompute', 'warning'); return }
      body.scope = 'day'
      body.crewId = compareCrewId
      body.date = compareDate || new Date().toISOString().slice(0, 10)
    } else if (compareLevel === 'crew-week') {
      if (!compareCrewId) { onSnackbar('Pick a route first for route-week exact recompute', 'warning'); return }
      body.scope = 'week'
      body.crewId = compareCrewId
      body.weekOf = compareDate || new Date().toISOString().slice(0, 10)
    } else if (compareLevel === 'two-week') {
      if (!compareCrewId) { onSnackbar('Pick a route first for two-week exact recompute', 'warning'); return }
      body.scope = 'week'
      body.crewId = compareCrewId
      body.weekOf = compareDate || new Date().toISOString().slice(0, 10)
    } else {
      body.scope = 'scenario'
      body.weekOf = compareDate || new Date().toISOString().slice(0, 10)
    }

    setRecomputingExact(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/recompute-exact`, body)
      const payload = response.data || null
      setExactRecompute(payload)

      if (body.scope === 'day' && payload?.recompute?.day) {
        const exactDay = payload.recompute.day
        setScenarioCompare(prev => {
          if (!prev?.comparison || prev.comparison.level !== 'crew-day') return prev
          const currentDate = toYmd(prev.comparison.date)
          const targetDate = toYmd(body.date)
          if (String(prev.comparison.crewId || '') !== String(body.crewId || '') || currentDate !== targetDate) return prev
          const baseline = prev.comparison.baseline || {}
          const scenario = {
            ...prev.comparison.scenario,
            stops: Number(exactDay.stops || 0),
            serviceMin: Number(exactDay.serviceMin || 0),
            driveMin: Number(exactDay.driveMin || 0),
            totalMin: Number(exactDay.totalMin || 0),
            otMin: Number(exactDay.otMin || 0),
            mode: exactDay.mode || 'exact'
          }
          const delta = {}
          Object.keys({ ...baseline, ...scenario }).forEach(key => {
            const sv = Number(scenario[key])
            const bv = Number(baseline[key])
            if (Number.isFinite(sv) && Number.isFinite(bv)) {
              delta[key] = Math.round((sv - bv) * 100) / 100
            }
          })
          return { ...prev, comparison: { ...prev.comparison, scenario, delta } }
        })
      }

      const summary = payload?.recompute?.summary || payload?.recompute?.fleet || null
      const exactDays = summary?.exactDays || 0
      const estimatedDays = summary?.estimatedDays || 0
      const callsAttempted = summary?.orsCallsAttempted || 0
      const callsSucceeded = summary?.orsCallsSucceeded || 0
      onSnackbar(
        `Exact recompute complete (${exactDays} exact / ${estimatedDays} est, ORS ${callsSucceeded}/${callsAttempted})`,
        estimatedDays > 0 ? 'warning' : 'success'
      )
    } catch (err) {
      console.error('Error recomputing exact metrics:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to recompute exact metrics', 'error')
    } finally {
      setRecomputingExact(false)
    }
  }

  const comparison = scenarioCompare?.comparison || null
  const baselineData = comparison?.baseline || null
  const scenarioData = comparison?.scenario || null
  const deltaData = comparison?.delta || {}

  const formatDelta = (val) => {
    if (val == null || !Number.isFinite(val)) return '-'
    const rounded = Math.round(val)
    if (rounded > 0) return `+${rounded}`
    return String(rounded)
  }

  const exactSummary = useMemo(() => {
    const payload = exactRecompute?.recompute || {}
    return payload.summary || payload.fleet || null
  }, [exactRecompute])

  const metricKeys = useMemo(() => {
    if (!baselineData && !scenarioData) return []
    const numericKeys = ['stops', 'serviceMin', 'driveMin', 'totalMin', 'otMin', 'avgCrewLoad', 'maxCrewLoad', 'totalProperties']
    const allKeys = new Set([...Object.keys(baselineData || {}), ...Object.keys(scenarioData || {})])
    return numericKeys.filter(k => allKeys.has(k))
  }, [baselineData, scenarioData])

  const isLoading = loadingCompare || loadingDiff || recomputingExact

  /* ── headline delta pills from fleet stats ──────────────────────── */
  const headlinePills = useMemo(() => {
    if (!baselineData || !scenarioData) return []
    const pills = []
    const add = (label, key, unit, invertBad) => {
      const sv = Number(scenarioData[key] || 0)
      const bv = Number(baselineData[key] || 0)
      const delta = Math.round((sv - bv) * 100) / 100
      const bad = invertBad ? delta < 0 : delta > 0
      pills.push({ label, sv, bv, delta, unit, bad })
    }
    add('Total OT', 'totalOT', ' min', false)
    add('Avg Crew Load', 'avgCrewLoad', ' min', false)
    if (scenarioData.totalProperties != null || baselineData.totalProperties != null) {
      const sv = Number(scenarioData.totalProperties || 0)
      const bv = Number(baselineData.totalProperties || 0)
      const delta = sv - bv
      pills.push({ label: 'Unassigned', sv, bv, delta, unit: '', bad: delta < 0 })
    }
    return pills
  }, [baselineData, scenarioData])

  /* ── fleet delta rows for settings drawer ───────────────────────── */
  const fleetDeltaRows = useMemo(() => {
    if (!baselineData || !scenarioData) return []
    return FLEET_METRIC_ROWS
      .filter(row => scenarioData[row.key] != null || baselineData[row.key] != null)
      .map(row => {
        const sv = Number(scenarioData[row.key] || 0)
        const bv = Number(baselineData[row.key] || 0)
        const dv = Math.round((sv - bv) * 100) / 100
        const isBad = (row.label.includes('OT') || row.label.includes('Unassigned')) ? dv > 0 : dv < 0
        return { ...row, sv, bv, dv, isBad }
      })
  }, [baselineData, scenarioData])

  const scenarioName = scenarios.find(s => s._id === selectedScenarioId)?.name || 'Scenario'
  const compareToLabel = compareToScenarioId
    ? (scenarios.find(s => s._id === compareToScenarioId)?.name || 'Scenario')
    : 'Published (live)'

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      {/* ── Sticky header ─────────────────────────────────────────── */}
      <div style={{
        flexShrink: 0, background: 'var(--df-surface)',
        borderBottom: '1px solid var(--df-line)', padding: '13px 20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* Scenario vs target label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink)' }}>{scenarioName}</span>
            <span style={{ fontSize: 12, color: 'var(--df-ink-4)' }}>vs</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--df-ink-3)' }}>{compareToLabel}</span>
          </div>

          {/* Segmented control */}
          <div className="seg-control" style={{ width: 320 }}>
            {LEVEL_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={'seg-btn' + (compareLevel === opt.value ? ' active' : '')}
                onClick={() => setCompareLevel(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Compare settings button */}
          <button
            onClick={() => setSettingsOpen(true)}
            style={{
              marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '7px 12px', borderRadius: 7, border: '1px solid var(--df-line)',
              background: 'var(--df-surface)', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 12, fontWeight: 600, color: 'var(--df-primary)'
            }}
          >
            <TuneIcon style={{ fontSize: 15 }} /> Compare settings
          </button>

          {isLoading && <CircularProgress size={18} style={{ color: 'var(--df-primary)' }} />}
        </div>

        {/* Headline delta pills */}
        {headlinePills.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 11, flexWrap: 'wrap' }}>
            {headlinePills.map(h => {
              const neutral = h.delta === 0
              return (
                <div key={h.label} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                  borderRadius: 8, border: '1px solid var(--df-line)', background: 'var(--df-surface-2)'
                }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--df-ink-3)' }}>{h.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink)' }}>
                    {Math.round(h.sv * 10) / 10}{h.unit}
                  </span>
                  <span style={{
                    fontSize: 11.5, fontWeight: 700,
                    color: neutral ? 'var(--df-ink-4)' : h.bad ? '#a9341d' : '#2f7a3e'
                  }}>
                    {h.delta > 0 ? '+' : ''}{Math.round(h.delta)}{h.unit}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Main scroll: charts + tables ──────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {/* Fleet Stats table */}
        {comparison && baselineData && scenarioData && (
          <div className="rw-card" style={{ marginBottom: 14, overflow: 'hidden' }}>
            <div style={{
              padding: '10px 14px', borderBottom: '1px solid var(--df-line)',
              display: 'flex', alignItems: 'center', gap: 8
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--df-ink)' }}>Fleet Stats</span>
              {comparison.level && (
                <span className="rw-pill pill-freq">Level: {comparison.level}</span>
              )}
              <span className="rw-pill pill-due">
                {scenarioCompare?.compareTo?.name || 'Published (live)'}
              </span>
              {comparison.crewName && (
                <span className="rw-pill pill-freq">Crew: {comparison.crewName}</span>
              )}
              {comparison.level === 'crew-day' && (
                <>
                  <Tooltip title="How scenario metrics were computed" arrow>
                    <span className="rw-pill pill-dist">
                      Scenario: {scenarioData?.mode || 'estimated'}
                    </span>
                  </Tooltip>
                  <Tooltip title="How baseline metrics were computed" arrow>
                    <span className="rw-pill pill-dist">
                      Baseline: {baselineData?.mode || 'estimated'}
                    </span>
                  </Tooltip>
                </>
              )}
            </div>
            <div className="tbl-wrap" style={{ maxHeight: 340 }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Scenario</th>
                    <th>Baseline</th>
                    <th>Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {FLEET_METRIC_ROWS
                    .filter(row => scenarioData[row.key] != null || baselineData[row.key] != null)
                    .map(row => {
                      const sv = Number(scenarioData[row.key] || 0)
                      const bv = Number(baselineData[row.key] || 0)
                      const dv = Math.round((sv - bv) * 100) / 100
                      const isBad = (row.label.includes('OT')) ? dv > 0 : dv < 0
                      return (
                        <tr key={row.key}>
                          <td style={{ fontWeight: 600, fontSize: 12, color: 'var(--df-ink-3)' }}>{row.label}</td>
                          <td style={{ fontSize: 12 }}>{Math.round(sv * 10) / 10}{row.unit}</td>
                          <td style={{ fontSize: 12 }}>{Math.round(bv * 10) / 10}{row.unit}</td>
                          <td style={{
                            fontWeight: 700, fontSize: 12,
                            color: dv === 0 ? 'var(--df-ink-4)' : isBad ? '#a9341d' : '#2f7a3e'
                          }}>
                            {dv > 0 ? '+' : ''}{Math.round(dv * 10) / 10}{row.unit}
                            {row.key === 'totalOT' && dv > 0 ? ' ⚠' : ''}
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Comparison metric table (per-level) */}
        {metricKeys.length > 0 && comparison?.level !== 'two-week' && (
          <div className="rw-card" style={{ marginBottom: 14, overflow: 'hidden' }}>
            <div style={{
              padding: '10px 14px', borderBottom: '1px solid var(--df-line)',
              display: 'flex', alignItems: 'center', gap: 8
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--df-ink)' }}>Comparison</span>
            </div>
            <div className="tbl-wrap" style={{ maxHeight: 340 }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Scenario</th>
                    <th>Baseline</th>
                    <th>Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {metricKeys.map(key => {
                    const sv = scenarioData?.[key]
                    const bv = baselineData?.[key]
                    const dv = deltaData?.[key]
                    const isBad = key === 'otMin' ? dv > 0 : dv < 0
                    return (
                      <tr key={key}>
                        <td style={{ fontWeight: 600, fontSize: 12, color: 'var(--df-ink-3)' }}>{key}</td>
                        <td style={{ fontSize: 12 }}>{sv != null ? Math.round(sv) : '-'}</td>
                        <td style={{ fontSize: 12 }}>{bv != null ? Math.round(bv) : '-'}</td>
                        <td style={{
                          fontWeight: 700, fontSize: 12,
                          color: !dv ? 'var(--df-ink-4)' : isBad ? '#a9341d' : '#2f7a3e'
                        }}>
                          {formatDelta(dv)}
                          {key === 'otMin' && dv > 0 ? ' ⚠' : ''}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {comparison && scenarioData?.mode && (
              <div style={{
                padding: '8px 14px', borderTop: '1px solid var(--df-line)',
                fontSize: 11, color: 'var(--df-ink-4)'
              }}>
                Mode: {scenarioData.mode}
                {exactSummary
                  ? ` | Last exact: ${exactSummary.exactDays || 0} exact / ${exactSummary.estimatedDays || 0} est`
                  : ' | Last exact: never'}
              </div>
            )}
          </div>
        )}

        {!comparison && !loadingCompare && (
          <div className="rw-card" style={{ padding: 24, textAlign: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--df-ink-4)' }}>
              No comparison loaded yet. Select a scenario above.
            </span>
          </div>
        )}

        {/* Fleet Charts */}
        {compareLevel === 'fleet' && comparison?.scenarioCrews?.length > 0 && (
          <Box mt={2}>
            <FleetCharts
              scenarioCrews={comparison.scenarioCrews}
              baselineCrews={comparison.baselineCrews}
              crewIds={crewIds}
            />
          </Box>
        )}

        {/* Two-Week View */}
        {compareLevel === 'two-week' && comparison?.level === 'two-week' && (
          <Box mt={2}>
            <TwoWeekView comparison={comparison} />
          </Box>
        )}

        {/* Crew-Day Route Detail */}
        {compareLevel === 'crew-day' && compareCrewId && (
          <Box mt={2}>
            <CrewDayRouteDetail
              url={url}
              selectedScenarioId={selectedScenarioId}
              crewId={compareCrewId}
              crewName={crews.find(c => c._id === compareCrewId)?.name || compareCrewId}
              date={compareDate}
              crewIds={crewIds || []}
              onSnackbar={onSnackbar}
            />
          </Box>
        )}
      </div>

      {/* ── Settings drawer ───────────────────────────────────────── */}
      {settingsOpen && <div className="cap-drawer-scrim" onClick={() => setSettingsOpen(false)} />}
      {settingsOpen && (
        <div className="cap-drawer">
          <div style={{
            padding: '14px 16px', borderBottom: '1px solid var(--df-line)',
            flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8
          }}>
            <TuneIcon style={{ fontSize: 16, color: 'var(--df-primary)' }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink)', flex: 1 }}>Compare Settings</span>
            <button
              onClick={() => setSettingsOpen(false)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', padding: 4, borderRadius: 4, color: 'var(--df-ink-3)'
              }}
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>

          <div style={{ overflowY: 'auto', flex: 1, padding: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Compare to */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--df-ink-3)', marginBottom: 4 }}>Compare to</div>
                <Select
                  value={compareToScenarioId}
                  onChange={(e) => setCompareToScenarioId(e.target.value)}
                  variant="outlined"
                  fullWidth
                  displayEmpty
                  style={{ fontSize: 13 }}
                >
                  <MenuItem value="">Published (live)</MenuItem>
                  {scenarios.filter(item => item._id !== selectedScenarioId).map(item => (
                    <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
                  ))}
                </Select>
              </div>

              {/* Level */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--df-ink-3)', marginBottom: 4 }}>Level</div>
                <Select
                  value={compareLevel}
                  onChange={(e) => setCompareLevel(e.target.value)}
                  variant="outlined"
                  fullWidth
                  style={{ fontSize: 13 }}
                >
                  <MenuItem value="fleet">Fleet</MenuItem>
                  <MenuItem value="crew-week">Route - Week</MenuItem>
                  <MenuItem value="crew-day">Route - Day</MenuItem>
                  <MenuItem value="two-week">Two-Week</MenuItem>
                </Select>
              </div>

              {/* Route (when not fleet) */}
              {compareLevel !== 'fleet' && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--df-ink-3)', marginBottom: 4 }}>Route</div>
                  <Select
                    value={compareCrewId}
                    onChange={(e) => setCompareCrewId(e.target.value)}
                    variant="outlined"
                    fullWidth
                    displayEmpty
                    style={{ fontSize: 13 }}
                  >
                    <MenuItem value="" disabled>Select route...</MenuItem>
                    {(routes && routes.length > 0)
                      ? routes.map(route => (
                        <MenuItem key={route._id} value={route.crewId || route._id}>{route.name}</MenuItem>
                      ))
                      : crews.map(crew => (
                        <MenuItem key={crew._id} value={crew._id}>{crew.name}</MenuItem>
                      ))
                    }
                  </Select>
                </div>
              )}

              {/* Date picker for crew-day / two-week */}
              {(compareLevel === 'crew-day' || compareLevel === 'two-week') && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--df-ink-3)', marginBottom: 4 }}>Date</div>
                  <TextField
                    type="date"
                    value={compareDate}
                    onChange={(e) => setCompareDate(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 6 }}>
                <DFButton
                  size="small"
                  variant="outlined"
                  disabled={!selectedScenarioId || isLoading}
                  onClick={() => {
                    fetchScenarioDiff(selectedScenarioId)
                    fetchScenarioCompare(selectedScenarioId)
                  }}
                  style={{ flex: 1 }}
                >
                  <RefreshIcon style={{ fontSize: 15, marginRight: 4 }} /> Refresh
                </DFButton>
                <DFButton
                  size="small"
                  variant="outlined"
                  disabled={!selectedScenarioId || isLoading}
                  onClick={runRecomputeExact}
                  style={{ flex: 1 }}
                >
                  <FlashOnIcon style={{ fontSize: 15, marginRight: 4 }} />
                  {recomputingExact ? 'Recomputing...' : 'Recompute Exact'}
                </DFButton>
              </div>
            </div>

            {/* Fleet delta vs Baseline */}
            {fleetDeltaRows.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4)',
                  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8
                }}>
                  Fleet Δ vs Baseline
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {fleetDeltaRows.map(m => (
                    <div key={m.label} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 8, padding: '7px 0', borderBottom: '1px solid var(--df-line)'
                    }}>
                      <span style={{ fontSize: 12, color: 'var(--df-ink-3)' }}>{m.label}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--df-ink)' }}>
                          {Math.round(m.sv * 10) / 10}{m.unit}
                        </span>
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          color: m.dv === 0 ? 'var(--df-ink-4)' : m.isBad ? '#a9341d' : '#2f7a3e',
                          minWidth: 36, textAlign: 'right'
                        }}>
                          {m.dv > 0 ? '+' : ''}{Math.round(m.dv * 10) / 10}{m.unit}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
